import { useEffect, useState } from "react";
import { Copy, Printer, ShieldCheck, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useToast } from "@/hooks/use-toast";

type Step = "scan" | "verify" | "codes";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: () => void;
  /** When true, disables Cancel — used for forced enrollment after admin reset. */
  forced?: boolean;
}

export default function TwoFactorEnrollment({
  open,
  onOpenChange,
  onComplete,
  forced = false,
}: Props) {
  const { toast } = useToast();
  const [step, setStep] = useState<Step>("scan");
  const [qrDataUrl, setQrDataUrl] = useState<string>("");
  const [manualSecret, setManualSecret] = useState<string>("");
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Start enrollment when dialog opens
  useEffect(() => {
    if (!open) {
      // Reset state when closed
      setStep("scan");
      setQrDataUrl("");
      setManualSecret("");
      setCode("");
      setBackupCodes([]);
      setSavedConfirmed(false);
      setError("");
      return;
    }
    void startEnrollment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function startEnrollment() {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/auth/2fa/enroll/start", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || "Failed to start enrollment.");
        return;
      }
      const data = await res.json();
      setQrDataUrl(data.qrDataUrl);
      setManualSecret(data.manualSecret);
    } catch {
      setError("Network error. Try again.");
    } finally {
      setLoading(false);
    }
  }

  async function confirmCode(e?: React.FormEvent) {
    e?.preventDefault();
    if (code.length !== 6 || loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/auth/2fa/enroll/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || "Invalid code. Try again.");
        setCode("");
        return;
      }
      const data = await res.json();
      setBackupCodes(data.backupCodes);
      setStep("codes");
    } catch {
      setError("Network error. Try again.");
    } finally {
      setLoading(false);
    }
  }

  function copyAll() {
    navigator.clipboard
      .writeText(backupCodes.join("\n"))
      .then(() => toast({ title: "Copied", description: "Backup codes copied to clipboard." }))
      .catch(() => toast({ title: "Copy failed", variant: "destructive" }));
  }

  function printCodes() {
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(`
      <html><head><title>Administr 2FA Backup Codes</title>
      <style>body{font-family:monospace;padding:24px}h1{font-family:sans-serif}.code{font-size:18px;letter-spacing:2px;padding:4px 0}</style>
      </head><body>
        <h1>Administr — 2FA Backup Codes</h1>
        <p>Each code works once. Store these somewhere safe (password manager, printed and locked).</p>
        ${backupCodes.map((c) => `<div class="code">${c.slice(0, 5)}-${c.slice(5)}</div>`).join("")}
        <p style="margin-top:32px;font-family:sans-serif;color:#666">Generated: ${new Date().toLocaleString()}</p>
      </body></html>
    `);
    win.document.close();
    win.print();
  }

  function done() {
    onOpenChange(false);
    onComplete?.();
  }

  // While on the codes step (or in forced mode), don't allow accidental close
  function handleOpenChange(next: boolean) {
    if (!next && (step === "codes" || forced)) {
      // Block ESC / outside click — must click Done
      return;
    }
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        onPointerDownOutside={(e) => {
          if (step === "codes" || forced) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (step === "codes" || forced) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#1737A2]" />
            Enable Two-Factor Authentication
          </DialogTitle>
          <DialogDescription>
            {step === "scan" &&
              "Scan the QR code with an authenticator app (Google Authenticator, Authy, 1Password, etc.)."}
            {step === "verify" &&
              "Enter the 6-digit code from your authenticator app to confirm."}
            {step === "codes" &&
              "Save these backup codes — they let you sign in if you lose your phone. Each works once."}
          </DialogDescription>
        </DialogHeader>

        {error && <div className="text-red-500 text-sm">{error}</div>}

        {step === "scan" && (
          <div className="space-y-4">
            <div className="flex justify-center">
              {loading || !qrDataUrl ? (
                <div className="h-48 w-48 flex items-center justify-center bg-gray-50 rounded">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <img
                  src={qrDataUrl}
                  alt="2FA QR code"
                  className="h-48 w-48 rounded border"
                />
              )}
            </div>
            {manualSecret && (
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Can't scan? Enter this key manually:
                <div className="mt-1 font-mono break-all bg-gray-50 dark:bg-gray-800 p-2 rounded">
                  {manualSecret}
                </div>
              </div>
            )}
          </div>
        )}

        {step === "verify" && (
          <form onSubmit={confirmCode} className="space-y-4">
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={code}
                onChange={setCode}
                autoFocus
              >
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
          </form>
        )}

        {step === "codes" && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-gray-50 dark:bg-gray-800 p-3 rounded">
              {backupCodes.map((c) => (
                <div key={c}>{c.slice(0, 5)}-{c.slice(5)}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={copyAll}>
                <Copy className="h-4 w-4 mr-1" /> Copy all
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={printCodes}>
                <Printer className="h-4 w-4 mr-1" /> Print
              </Button>
            </div>
            <label className="flex items-center gap-2 text-sm pt-2">
              <Checkbox
                checked={savedConfirmed}
                onCheckedChange={(v) => setSavedConfirmed(v === true)}
              />
              I have saved these codes in a safe place
            </label>
          </div>
        )}

        <DialogFooter>
          {!forced && step !== "codes" && (
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
          )}
          {step === "scan" && (
            <Button
              onClick={() => setStep("verify")}
              disabled={loading || !qrDataUrl}
              className="bg-[#1737A2] hover:bg-[#102366]"
            >
              Next
            </Button>
          )}
          {step === "verify" && (
            <Button
              onClick={() => confirmCode()}
              disabled={code.length !== 6 || loading}
              className="bg-[#1737A2] hover:bg-[#102366]"
            >
              {loading ? "Verifying..." : "Verify & Continue"}
            </Button>
          )}
          {step === "codes" && (
            <Button
              onClick={done}
              disabled={!savedConfirmed}
              className="bg-[#1737A2] hover:bg-[#102366]"
            >
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
